import React, { useState, useEffect } from 'react';
import { Routes, Route, NavLink } from 'react-router-dom';
import { Calendar, Users, DollarSign, Percent, Settings, LogOut, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Bookings from './Bookings';
import Clients from './Clients';
import Finances from './Finances';
import Commissions from './Commissions';
import Configuration from './Configuration';

const Dashboard = ({ user, onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Set page title
  useEffect(() => {
    document.title = 'Panel Administrativo - MAUMA';
  }, []);

  const canViewFinances = user.role === 'admin';
  const canViewCommissions = user.role === 'admin' || user.role === 'operaria';
  const canViewConfig = user.role === 'admin';

  const navItems = [
    { path: '', label: 'Reservas', icon: Calendar, show: true },
    { path: 'clients', label: 'Clientes', icon: Users, show: true },
    { path: 'finances', label: 'Finanzas', icon: DollarSign, show: canViewFinances },
    { path: 'commissions', label: 'Comisiones', icon: Percent, show: canViewCommissions },
    { path: 'config', label: 'Configuración', icon: Settings, show: canViewConfig },
  ];

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-[#C9A961]/20 transition-transform duration-300 ease-in-out`}
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-[#C9A961]/20">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-cormorant font-bold text-[#C9A961]">MAUMA</h1>
                <p className="text-xs text-[#6B6B6B] mt-1">Sistema de Gestión</p>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden text-[#6B6B6B] hover:text-[#C9A961]"
                data-testid="close-sidebar-button"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          {/* User Info */}
          <div className="p-4 border-b border-[#C9A961]/10">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-[#C9A961] flex items-center justify-center">
                <span className="text-white font-semibold text-sm">
                  {user.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#2C2C2C] truncate">{user.name}</p>
                <p className="text-xs text-[#6B6B6B] capitalize">{user.role}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems
              .filter((item) => item.show)
              .map((item) => (
                <NavLink
                  key={item.path}
                  to={`/admin/${item.path}`}
                  end={item.path === ''}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center space-x-3 px-4 py-3 rounded-lg font-medium text-sm transition-colors ${
                      isActive
                        ? 'bg-[#C9A961] text-white'
                        : 'text-[#6B6B6B] hover:bg-[#F5F2ED] hover:text-[#C9A961]'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <item.icon size={20} />
                      <span>{item.label}</span>
                    </>
                  )}
                </NavLink>
              ))}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-[#C9A961]/20">
            <Button
              onClick={onLogout}
              data-testid="logout-button"
              variant="outline"
              className="w-full justify-start space-x-3 border-[#C9A961]/30 text-[#6B6B6B] hover:bg-[#F5F2ED] hover:text-[#C9A961] hover:border-[#C9A961]"
            >
              <LogOut size={20} />
              <span>Cerrar Sesión</span>
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile Header */}
        <header className="lg:hidden bg-white border-b border-[#C9A961]/20 p-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-[#6B6B6B] hover:text-[#C9A961]"
              data-testid="open-sidebar-button"
            >
              <Menu size={24} />
            </button>
            <h1 className="text-xl font-cormorant font-bold text-[#C9A961]">MAUMA</h1>
            <div className="w-6" />
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          <Routes>
            <Route index element={<Bookings user={user} />} />
            <Route path="clients" element={<Clients user={user} />} />
            {canViewFinances && <Route path="finances" element={<Finances user={user} />} />}
            {canViewCommissions && <Route path="commissions" element={<Commissions user={user} />} />}
            {canViewConfig && <Route path="config" element={<Configuration user={user} />} />}
          </Routes>
        </main>
      </div>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;